import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn } from '@/Components/motion';
import { Link } from 'react-router-dom';
import { Separator } from '@/Components/ui/separator';
import { siteConfig } from '@/config/site';

const sections = [
    {
        title: 'Introduction',
        content:
            `These Terms and Conditions ("Terms") govern your access to and use of the ${siteConfig.brand.name} website, platform, and services. By accessing or using our services, you agree to be bound by these Terms in their entirety. If you do not agree with any part of these Terms, you must not use our services. ${siteConfig.brand.name} reserves the right to update or modify these Terms at any time without prior notice. Continued use of our services after any changes constitutes acceptance of the revised Terms.`,
    },
    {
        title: 'Eligibility',
        content:
            `You must be at least 18 years of age (or the age of legal majority in your jurisdiction) to use ${siteConfig.brand.name} services. By registering an account, you represent and warrant that you meet the minimum age requirement and have the legal capacity to enter into a binding agreement. ${siteConfig.brand.name} services are not available to residents of restricted countries or jurisdictions where such services are prohibited by law. It is your responsibility to ensure that your use of our services complies with all applicable local, state, national, and international laws and regulations.`,
    },
    {
        title: 'Account Usage',
        content:
            `You are responsible for maintaining the confidentiality of your account credentials, including your username and password. You agree to notify ${siteConfig.brand.name} immediately of any unauthorized access to or use of your account. You are solely responsible for all activities that occur under your account. ${siteConfig.brand.name} reserves the right to suspend or terminate your account at any time if we reasonably believe that your account has been compromised, is being used fraudulently, or is in violation of these Terms.`,
    },
    {
        title: 'Trading Rules',
        content:
            `All traders using ${siteConfig.brand.name} services must adhere to the trading rules and guidelines established for each account type. This includes compliance with maximum drawdown limits, daily loss limits, position sizing requirements, and any other parameters specified in your account plan. The use of prohibited trading strategies such as high-frequency trading exploits, latency arbitrage, or any form of platform manipulation is strictly forbidden. Violations of trading rules may result in account suspension, profit forfeiture, or permanent termination of your account.`,
    },
    {
        title: 'Intellectual Property',
        content:
            `All content, trademarks, logos, graphics, software, and other materials on the ${siteConfig.brand.name} platform are the intellectual property of ${siteConfig.brand.name} or its licensors. You may not reproduce, distribute, modify, create derivative works from, publicly display, or otherwise exploit any content from our platform without prior written consent from ${siteConfig.brand.name}. Any unauthorized use of our intellectual property may result in legal action.`,
    },
    {
        title: 'Limitation of Liability',
        content:
            `To the maximum extent permitted by applicable law, ${siteConfig.brand.name} and its directors, officers, employees, agents, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or in connection with your use of our services. This includes, but is not limited to, loss of profits, data, goodwill, or other intangible losses. ${siteConfig.brand.name}\'s total liability to you for all claims shall not exceed the amount you have paid to ${siteConfig.brand.name} in the twelve (12) months preceding the event giving rise to the claim.`,
    },
    {
        title: 'Modifications',
        content:
            `${siteConfig.brand.name} reserves the right to modify, suspend, or discontinue any part of our services at any time, with or without notice. We may also revise these Terms from time to time. Material changes will be communicated through our platform or via email. Your continued use of our services following the posting of revised Terms means you accept and agree to the changes. It is your responsibility to review these Terms periodically for updates.`,
    },
    {
        title: 'Governing Law',
        content:
            `These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which ${siteConfig.brand.name} is registered, without regard to its conflict of law provisions. Any disputes arising out of or related to these Terms or your use of our services shall be resolved through binding arbitration in accordance with the rules of the applicable arbitration authority. You agree to submit to the exclusive jurisdiction of the courts in the governing jurisdiction for any legal proceedings.`,
    },
    {
        title: 'Contact',
        content:
            'If you have any questions, concerns, or requests regarding these Terms and Conditions, please contact us at info@teffunded.com. You may also reach out to our support team through the contact page on our website. We are committed to addressing your inquiries promptly and transparently.',
    },
];

export default function TermsAndConditions() {
    return (
        <AppLayout title={`Terms & Conditions - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                            Legal
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Terms &{' '}
                            <span className="text-gradient">Conditions</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                            Please read these terms carefully before using
                            {siteConfig.brand.name} services.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Content */}
            <Section className="py-16 bg-muted/30 border-t border-border">
                <FadeIn>
                    <Card className="max-w-4xl mx-auto rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                        <CardContent className="p-8 md:p-12">
                            <p className="text-sm text-muted-foreground mb-10">
                                Last updated: March 2026
                            </p>

                            <div className="space-y-10">
                                {sections.map((section, index) => (
                                    <div key={section.title}>
                                        <h2 className="text-xl font-semibold text-foreground mb-3">
                                            {index + 1}. {section.title}
                                        </h2>
                                        <p className="text-muted-foreground leading-relaxed">
                                            {section.content}
                                        </p>
                                        {index < sections.length - 1 && (
                                            <Separator className="mt-10" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </FadeIn>
            </Section>

            {/* Bottom spacer */}
            <Section className="py-16 bg-background border-t border-border">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-sm text-muted-foreground">
                        Questions about these terms?{' '}
                        <Link
                            to="/contact"
                            className="text-primary hover:underline"
                        >
                            Contact us
                        </Link>
                    </p>
                </div>
            </Section>
        </AppLayout>
    );
}
